import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/api/axios";
import toast from "react-hot-toast";
import usePageTitle from "../../utils/hooks/usePageTitle";
import DeletionCertificateModal from "./components/DeletionCertificateModal";
import { CompanyQuickViewDrawer } from "../../components/companies/CompanyQuickViewDrawer";
import {
  MdBusiness,
  MdTimer,
  MdAssignment,
  MdAutorenew,
  MdSearch,
  MdDownload,
  MdEmail,
  MdRefresh,
  MdNotificationsActive,
  MdClose,
  MdWarning,
  MdAdd,
  MdHistory,
  MdVisibility,
  MdFlashOn,
  MdBugReport
} from "react-icons/md";

export interface Contact {
  user_id: number;
  name: string;
  email: string;
}

export interface SubscriptionRecord {
  subscription_id: number;
  company_id: number;
  subscription_name?: string | null;
  subscription_type: string;
  start_date: string;
  end_date: string;
  quick_runs_quota: number;
  comprehensive_runs_quota: number;
  created_at: string;
}

export interface OrganizationOverview {
  company_id: number;
  company_name: string;
  company_logo: string | null;
  subscription_name?: string | null;
  subscription_type: string;
  is_one_time: boolean;
  is_unassigned?: boolean;
  quick_runs_quota: number;
  comprehensive_runs_quota: number;
  quick_run_duration: number;
  comprehensive_run_duration: number;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  retention_end_date: string | null;
  status: "active" | "expiring_soon" | "expired" | "unassigned";
  contact: Contact | null;
  last_program_created_at: string | null;
  has_program_this_month: boolean;
  last_deletion_notice_sent_at: string | null;
  last_program_reminder_sent_at: string | null;
  total_programs?: number;
  programs?: Array<{
    program_id: number;
    program_title: string;
    createdAt?: string | Date;
  }>;
  latest_program_title?: string | null;
  reminder_sent_this_month: boolean;
  created_at: string;
}

export interface SummaryMetrics {
  totalOrgs: number;
  expiringSoon: number;
  unassignedCount?: number;
  oneTimeCount: number;
  recurringCount: number;
  unscheduledCount?: number;
  pendingInitiationCount?: number;
  quotaExhaustedCount?: number;
}

const OrganizationsSubscriptions: React.FC = () => {
  usePageTitle("Organizations & Subscriptions");
  const navigate = useNavigate();

  const handleRowClick = (companyId: number) => {
    navigate(`/dashboard/companies/${companyId}?tab=subscription`);
  };

  const [organizations, setOrganizations] = useState<OrganizationOverview[]>([]);
  const [summary, setSummary] = useState<SummaryMetrics>({
    totalOrgs: 0,
    expiringSoon: 0,
    unassignedCount: 0,
    oneTimeCount: 0,
    recurringCount: 0,
    unscheduledCount: 0,
    pendingInitiationCount: 0,
    quotaExhaustedCount: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [healthSignalFilter, setHealthSignalFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<string>("company_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Selection & Bulk Actions state
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);

  // Quick View Inspection Drawer state
  const [isQuickViewOpen, setIsQuickViewOpen] = useState<boolean>(false);
  const [quickViewCompanyId, setQuickViewCompanyId] = useState<number | null>(null);
  const [quickViewCompanyName, setQuickViewCompanyName] = useState<string>("");
  const [quickViewOrg, setQuickViewOrg] = useState<OrganizationOverview | null>(null);

  // Quick Quota Top-Up Modal state
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState<boolean>(false);
  const [topUpCompanyId, setTopUpCompanyId] = useState<number | null>(null);
  const [topUpCompanyName, setTopUpCompanyName] = useState<string>("");
  const [topUpQuickAdd, setTopUpQuickAdd] = useState<number>(0);
  const [topUpCompAdd, setTopUpCompAdd] = useState<number>(0);
  const [topUpReason, setTopUpReason] = useState<string>("");

  // Standard Modals state
  const [selectedOrg, setSelectedOrg] = useState<OrganizationOverview | null>(null);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState<boolean>(false);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState<boolean>(false);
  const [isAddNewSub, setIsAddNewSub] = useState<boolean>(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);
  const [isCertModalOpen, setIsCertModalOpen] = useState<boolean>(false);
  const [selectedCertOrg, setSelectedCertOrg] = useState<OrganizationOverview | null>(null);
  const [subHistory, setSubHistory] = useState<SubscriptionRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Form states
  const [customMessage, setCustomMessage] = useState<string>("");
  const [reminderRecipientEmail, setReminderRecipientEmail] = useState<string>("");
  const [deletionRecipientEmail, setDeletionRecipientEmail] = useState<string>("");
  const [deletionPrograms, setDeletionPrograms] = useState<
    Array<{ program_id: number; program_title: string; createdAt?: any }>
  >([]);
  const [selectedDeletionProgramIds, setSelectedDeletionProgramIds] = useState<number[]>([]);
  const [customDeletionProgramTitle, setCustomDeletionProgramTitle] = useState<string>("");
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  // Renewal form fields
  const [renewSubName, setRenewSubName] = useState<string>("");
  const [renewType, setRenewType] = useState<string>("monthly");
  const [renewStartDate, setRenewStartDate] = useState<string>("");
  const [renewEndDate, setRenewEndDate] = useState<string>("");
  const [renewQuickQuota, setRenewQuickQuota] = useState<number>(0);
  const [renewCompQuota, setRenewCompQuota] = useState<number>(0);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/v2/companies/admin/subscriptions-overview");
      if (response.data) {
        setOrganizations(response.data.organizations || []);
        setSummary(
          response.data.summary || {
            totalOrgs: 0,
            expiringSoon: 0,
            unassignedCount: 0,
            oneTimeCount: 0,
            recurringCount: 0
          }
        );
      }
    } catch (error: any) {
      console.error("Error loading organizations overview:", error);
      toast.error(error?.response?.data?.message || "Failed to load organizations overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Smart Health Badges Helper
  const getSmartHealthBadge = (org: OrganizationOverview) => {
    if (org.is_unassigned || org.status === "unassigned") {
      return {
        key: "needs_setup",
        label: "Needs Setup",
        bg: "bg-amber-50 text-amber-900 border-amber-300",
        dot: "bg-amber-500",
        hint: "Assign subscription term & quotas"
      };
    }

    if (org.is_one_time && org.retention_end_date) {
      const now = new Date();
      const retDate = new Date(org.retention_end_date);
      const diffDays = Math.ceil((retDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
      if (diffDays <= 0) {
        return {
          key: "retention_expired",
          label: "Retention Expired",
          bg: "bg-purple-50 text-purple-900 border-purple-300",
          dot: "bg-purple-600",
          hint: "12-mo retention period complete"
        };
      }
      if (diffDays <= 30) {
        return {
          key: "deletion_approaching",
          label: `Deletion in ${diffDays}d`,
          bg: "bg-purple-50 text-purple-900 border-purple-300",
          dot: "bg-purple-500 animate-pulse",
          hint: "Send deletion notice email"
        };
      }
    }

    if (org.quick_runs_quota === 0 && org.comprehensive_runs_quota === 0) {
      return {
        key: "quota_exhausted",
        label: "Quota Exhausted",
        bg: "bg-rose-50 text-rose-900 border-rose-300",
        dot: "bg-rose-600 animate-pulse",
        hint: "0 runs remaining — top-up required"
      };
    }

    // One-Time without any programs launched: Pending Initiation
    if (org.is_one_time && (!org.total_programs || org.total_programs === 0) && !org.last_program_created_at) {
      return {
        key: "pending_initiation",
        label: "Pending Initiation",
        bg: "bg-cyan-50 text-cyan-900 border-cyan-300",
        dot: "bg-cyan-500",
        hint: "One-time engagement purchased — pentest not yet launched"
      };
    }

    // High Priority Alert: Subscription expiring within 30 days
    if (org.status === "expiring_soon") {
      return {
        key: "expiring_soon",
        label: "Expiring Soon",
        bg: "bg-amber-50 text-amber-900 border-amber-300",
        dot: "bg-amber-500 animate-pulse",
        hint: "Subscription expires within 30 days"
      };
    }

    // Recurring with no program this calendar month: Unscheduled Month
    if (!org.is_one_time && !org.has_program_this_month) {
      return {
        key: "unscheduled_month",
        label: "Unscheduled Month",
        bg: "bg-blue-50 text-blue-900 border-blue-300",
        dot: "bg-blue-500",
        hint: "No pentest created this month"
      };
    }

    return {
      key: "healthy",
      label: "Healthy",
      bg: "bg-emerald-50 text-emerald-900 border-emerald-300",
      dot: "bg-emerald-500",
      hint: "Active subscription with available quotas"
    };
  };

  // Computed summary metrics derived directly from loaded organizations using smart health badge priority
  const displaySummary = React.useMemo(() => {
    if (!organizations || organizations.length === 0) return summary;
    let unassigned = 0;
    let unscheduled = 0;
    let pendingInitiation = 0;
    let expiringSoon = 0;
    let quotaExhausted = 0;
    let recurring = 0;
    let oneTime = 0;

    organizations.forEach(org => {
      if (org.is_one_time) oneTime++;
      else if (!org.is_unassigned && org.status !== "unassigned") recurring++;

      const badge = getSmartHealthBadge(org);
      if (badge.key === "needs_setup") unassigned++;
      else if (badge.key === "quota_exhausted") quotaExhausted++;
      else if (badge.key === "pending_initiation") pendingInitiation++;
      else if (badge.key === "expiring_soon") expiringSoon++;
      else if (badge.key === "unscheduled_month") unscheduled++;
    });

    return {
      totalOrgs: organizations.length,
      unassignedCount: unassigned,
      unscheduledCount: unscheduled,
      pendingInitiationCount: pendingInitiation,
      expiringSoon: expiringSoon,
      quotaExhaustedCount: quotaExhausted,
      recurringCount: recurring,
      oneTimeCount: oneTime
    };
  }, [organizations, summary]);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, subscriptionTypeFilter, statusFilter, healthSignalFilter]);

  // Memoized filtered organizations
  const filteredOrgs = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return organizations.filter(org => {
      const matchesSearch =
        !q ||
        org.company_name.toLowerCase().includes(q) ||
        (org.contact?.name && org.contact.name.toLowerCase().includes(q)) ||
        (org.contact?.email && org.contact.email.toLowerCase().includes(q));

      if (!matchesSearch) return false;

      if (subscriptionTypeFilter === "one_time") {
        if (!org.is_one_time) return false;
      } else if (subscriptionTypeFilter === "recurring") {
        if (
          org.is_one_time ||
          org.is_unassigned ||
          org.subscription_type.toLowerCase() === "unassigned"
        )
          return false;
      } else if (subscriptionTypeFilter === "unassigned") {
        if (
          !org.is_unassigned &&
          org.subscription_type.toLowerCase() !== "unassigned"
        )
          return false;
      }

      if (statusFilter !== "all" && org.status !== statusFilter) {
        return false;
      }

      if (healthSignalFilter !== "all") {
        const badge = getSmartHealthBadge(org);
        if (badge.key !== healthSignalFilter) return false;
      }

      return true;
    });
  }, [organizations, searchQuery, subscriptionTypeFilter, statusFilter, healthSignalFilter]);

  // Sorting Handler
  const handleSort = useCallback((field: string) => {
    setSortOrder(prevOrder => {
      setSortField(prevField => {
        if (prevField === field) {
          return prevField;
        }
        return field;
      });
      return sortField === field ? (prevOrder === "asc" ? "desc" : "asc") : "asc";
    });
  }, [sortField]);

  // Memoized sorted organizations
  const sortedOrgs = useMemo(() => {
    if (!sortField) return filteredOrgs;
    return [...filteredOrgs].sort((a, b) => {
      let comparison = 0;
      if (sortField === "company_name") {
        comparison = a.company_name.localeCompare(b.company_name);
      } else if (sortField === "contact") {
        comparison = (a.contact?.name || "").localeCompare(b.contact?.name || "");
      } else if (sortField === "subscription") {
        comparison = (a.subscription_type || "").localeCompare(b.subscription_type || "");
      } else if (sortField === "quotas") {
        const quotaA = (a.quick_runs_quota || 0) + (a.comprehensive_runs_quota || 0);
        const quotaB = (b.quick_runs_quota || 0) + (b.comprehensive_runs_quota || 0);
        comparison = quotaA - quotaB;
      } else if (sortField === "health_signal") {
        const badgeA = getSmartHealthBadge(a).label;
        const badgeB = getSmartHealthBadge(b).label;
        comparison = badgeA.localeCompare(badgeB);
      } else if (sortField === "expiry") {
        const dateA = a.subscription_end_date ? new Date(a.subscription_end_date).getTime() : 0;
        const dateB = b.subscription_end_date ? new Date(b.subscription_end_date).getTime() : 0;
        comparison = dateA - dateB;
      } else if (sortField === "last_contact") {
        const dateA = Math.max(
          a.last_deletion_notice_sent_at ? new Date(a.last_deletion_notice_sent_at).getTime() : 0,
          a.last_program_reminder_sent_at ? new Date(a.last_program_reminder_sent_at).getTime() : 0
        );
        const dateB = Math.max(
          b.last_deletion_notice_sent_at ? new Date(b.last_deletion_notice_sent_at).getTime() : 0,
          b.last_program_reminder_sent_at ? new Date(b.last_program_reminder_sent_at).getTime() : 0
        );
        comparison = dateA - dateB;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [filteredOrgs, sortField, sortOrder]);

  // Pagination calculations
  const totalItems = sortedOrgs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const paginatedOrgs = useMemo(() => {
    return sortedOrgs.slice(startIndex, startIndex + pageSize);
  }, [sortedOrgs, startIndex, pageSize]);

  // Checkbox Selection Handlers
  const handleSelectAll = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedOrgIds(paginatedOrgs.map(o => o.company_id));
    } else {
      setSelectedOrgIds([]);
    }
  }, [paginatedOrgs]);

  const handleToggleSelectOrg = useCallback((companyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOrgIds(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  }, []);

  // Selected Organizations eligibility helpers with O(1) set lookup
  const selectedOrgSet = useMemo(() => new Set(selectedOrgIds), [selectedOrgIds]);

  const selectedOrgsList = useMemo(() => {
    if (selectedOrgSet.size === 0) return [];
    return organizations.filter(o => selectedOrgSet.has(o.company_id));
  }, [organizations, selectedOrgSet]);

  // Reminder applicable: recurring active organizations (NOT Needs Setup / unassigned, NOT one-time)
  const reminderEligibleOrgs = useMemo(() => {
    return selectedOrgsList.filter(
      o => !o.is_unassigned && o.status !== "unassigned" && !o.is_one_time
    );
  }, [selectedOrgsList]);

  // Deletion notice applicable: any organization with assigned subscription/term (NOT Needs Setup / unassigned)
  const deletionEligibleOrgs = useMemo(() => {
    return selectedOrgsList.filter(
      o => !o.is_unassigned && o.status !== "unassigned"
    );
  }, [selectedOrgsList]);

  // Bulk Actions
  const handleBulkSendReminders = async () => {
    const eligibleIds = reminderEligibleOrgs.map(o => o.company_id);
    if (eligibleIds.length === 0) {
      toast.error("No selected organizations are eligible for program reminders (must be active recurring subscriptions)");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post("/v2/companies/admin/bulk-send-reminders", {
        company_ids: eligibleIds,
        custom_message: customMessage
      });
      toast.success(res.data.message || `Bulk reminders sent to ${eligibleIds.length} organization(s)`);
      setSelectedOrgIds([]);
      fetchOverview();
    } catch (error: any) {
      console.error("Bulk reminder error:", error);
      toast.error(error?.response?.data?.message || "Failed to send bulk reminders");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkSendDeletionNotices = async () => {
    const eligibleIds = deletionEligibleOrgs.map(o => o.company_id);
    if (eligibleIds.length === 0) {
      toast.error("No selected organizations are eligible for deletion notices (must have an assigned subscription/term)");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post("/v2/companies/admin/bulk-send-deletion-notices", {
        company_ids: eligibleIds,
        custom_message: customMessage
      });
      toast.success(res.data.message || `Bulk deletion notices sent to ${eligibleIds.length} organization(s)`);
      setSelectedOrgIds([]);
      fetchOverview();
    } catch (error: any) {
      console.error("Bulk deletion notice error:", error);
      toast.error(error?.response?.data?.message || "Failed to send bulk deletion notices");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Quick View Drawer
  const handleOpenQuickView = (org: OrganizationOverview, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickViewCompanyId(org.company_id);
    setQuickViewCompanyName(org.company_name);
    setQuickViewOrg(org);
    setIsQuickViewOpen(true);
  };

  // Open Quick Top Up Modal
  const handleOpenTopUpModal = (companyId: number, companyName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setTopUpCompanyId(companyId);
    setTopUpCompanyName(companyName);
    setTopUpQuickAdd(0);
    setTopUpCompAdd(1);
    setTopUpReason("Manual Top Up");
    setIsTopUpModalOpen(true);
  };

  // Submit Quick Quota Top-Up
  const handleTopUpSubmit = async () => {
    if (!topUpCompanyId) return;
    if (topUpQuickAdd === 0 && topUpCompAdd === 0) {
      toast.error("Please add at least 1 quota run");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`/v2/companies/admin/${topUpCompanyId}/topup-quota`, {
        quick_runs_add: topUpQuickAdd,
        comprehensive_runs_add: topUpCompAdd,
        reason: topUpReason
      });
      toast.success(res.data.message || "Quotas topped up successfully");
      setIsTopUpModalOpen(false);
      fetchOverview();
    } catch (error: any) {
      console.error("Top up error:", error);
      toast.error(error?.response?.data?.message || "Failed to top up quota");
    } finally {
      setActionLoading(false);
    }
  };

  // Export Filtered / Selected CSV
  const handleExportCSV = (selectedOnly: boolean = false) => {
    const listToExport = selectedOnly
      ? filteredOrgs.filter(o => selectedOrgSet.has(o.company_id))
      : filteredOrgs;

    if (listToExport.length === 0) {
      toast.error("No organizations available to export");
      return;
    }

    const safeISO = (d: string | null) => {
      if (!d) return "N/A";
      const date = new Date(d);
      return isNaN(date.getTime()) ? "N/A" : date.toISOString().split("T")[0];
    };

    const headers = [
      "Company ID",
      "Company Name",
      "Contact Name",
      "Contact Email",
      "Subscription Type",
      "Quick Quota",
      "Comp Quota",
      "Start Date",
      "Expiry Date",
      "Retention Date",
      "Status",
      "Has Program This Month",
      "Last Deletion Notice Sent",
      "Last Program Reminder Sent"
    ];

    const rows = listToExport.map(org => [
      org.company_id,
      `"${org.company_name.replace(/"/g, '""')}"`,
      `"${(org.contact?.name || "N/A").replace(/"/g, '""')}"`,
      `"${(org.contact?.email || "N/A").replace(/"/g, '""')}"`,
      `"${org.subscription_type}"`,
      org.quick_runs_quota,
      org.comprehensive_runs_quota,
      safeISO(org.subscription_start_date),
      safeISO(org.subscription_end_date),
      safeISO(org.retention_end_date),
      org.status,
      org.has_program_this_month ? "Yes" : "No",
      safeISO(org.last_deletion_notice_sent_at),
      safeISO(org.last_program_reminder_sent_at)
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Organizations_Subscriptions_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${listToExport.length} organization records`);
  };

  // Open Deletion Modal
  const handleOpenDeletionModal = (org: OrganizationOverview) => {
    setSelectedOrg(org);
    setCustomMessage("");
    setDeletionRecipientEmail(org.contact?.email || "");

    const progs = org.programs || [];
    setDeletionPrograms(progs);
    const progIds = progs.map(p => p.program_id);
    setSelectedDeletionProgramIds(progIds);

    const defaultTitle =
      progs.length > 0
        ? progs.map(p => p.program_title).filter(Boolean).join(", ")
        : (org.latest_program_title || `${org.company_name} Penetration Test`);
    setCustomDeletionProgramTitle(defaultTitle);
    setIsDeletionModalOpen(true);
  };

  const handleToggleDeletionProgram = (progId: number) => {
    setSelectedDeletionProgramIds(prev => {
      const isSelected = prev.includes(progId);
      const next = isSelected ? prev.filter(id => id !== progId) : [...prev, progId];
      const selected = deletionPrograms.filter(p => next.includes(p.program_id));
      if (selected.length > 0) {
        setCustomDeletionProgramTitle(
          selected.map(p => p.program_title).filter(Boolean).join(", ")
        );
      } else if (selectedOrg) {
        setCustomDeletionProgramTitle(`${selectedOrg.company_name} Penetration Test`);
      }
      return next;
    });
  };

  const handleSelectAllDeletionPrograms = () => {
    if (selectedDeletionProgramIds.length === deletionPrograms.length) {
      setSelectedDeletionProgramIds([]);
      if (selectedOrg) {
        setCustomDeletionProgramTitle(`${selectedOrg.company_name} Penetration Test`);
      }
    } else {
      const allIds = deletionPrograms.map(p => p.program_id);
      setSelectedDeletionProgramIds(allIds);
      setCustomDeletionProgramTitle(
        deletionPrograms.map(p => p.program_title).filter(Boolean).join(", ")
      );
    }
  };

  // Submit Deletion Notice Email
  const handleSendDeletionNotice = async () => {
    if (!selectedOrg) return;
    const targetEmail = deletionRecipientEmail.trim() || selectedOrg.contact?.email;
    if (!targetEmail) {
      toast.error("Please enter a valid recipient email address");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`/v2/companies/admin/${selectedOrg.company_id}/send-deletion-notice`, {
        recipient_email: targetEmail,
        custom_message: customMessage,
        program_ids: selectedDeletionProgramIds,
        program_title: customDeletionProgramTitle.trim()
      });
      toast.success(res.data.message || "Deletion notice sent successfully");
      setIsDeletionModalOpen(false);
      fetchOverview();
    } catch (error: any) {
      console.error("Deletion notice error:", error);
      toast.error(error?.response?.data?.message || "Failed to send deletion notice");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Renew / Assign Modal
  const handleOpenRenewModal = (org: OrganizationOverview) => {
    setSelectedOrg(org);
    setIsAddNewSub(false);
    setRenewSubName(org.subscription_name || `${org.company_name} Subscription`);
    setRenewType(org.subscription_type && org.subscription_type.toLowerCase() !== "unassigned" ? org.subscription_type : "monthly");
    setRenewStartDate(org.subscription_start_date ? new Date(org.subscription_start_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);

    let defaultEnd = org.subscription_end_date ? new Date(org.subscription_end_date).toISOString().split("T")[0] : "";
    if (!defaultEnd) {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      defaultEnd = d.toISOString().split("T")[0];
    }
    setRenewEndDate(defaultEnd);
    setRenewQuickQuota(org.quick_runs_quota || 0);
    setRenewCompQuota(org.comprehensive_runs_quota || 0);
    setIsRenewModalOpen(true);
  };

  // Open Add New Subscription Modal
  const handleOpenAddSubscriptionModal = (org: OrganizationOverview) => {
    setSelectedOrg(org);
    setIsAddNewSub(true);
    setRenewSubName(`${org.company_name} Subscription`);
    setRenewType("monthly");
    setRenewStartDate(new Date().toISOString().split("T")[0]);
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    setRenewEndDate(d.toISOString().split("T")[0]);
    setRenewQuickQuota(1);
    setRenewCompQuota(1);
    setIsRenewModalOpen(true);
  };

  // Submit Renew / Assign Subscription
  const handleRenewSubscription = async () => {
    if (!selectedOrg) return;
    setActionLoading(true);
    try {
      const endpoint = isAddNewSub
        ? `/v2/companies/admin/${selectedOrg.company_id}/add-subscription`
        : `/v2/companies/admin/${selectedOrg.company_id}/renew-subscription`;

      const res = await axios.post(endpoint, {
        subscription_name: renewSubName,
        subscription_type: renewType,
        subscription_start_date: renewStartDate,
        subscription_end_date: renewEndDate,
        quick_runs_quota: renewQuickQuota,
        comprehensive_runs_quota: renewCompQuota
      });

      toast.success(res.data.message || (isAddNewSub ? "New subscription term added" : "Subscription updated successfully"));
      setIsRenewModalOpen(false);
      fetchOverview();
    } catch (error: any) {
      console.error("Subscription save error:", error);
      toast.error(error?.response?.data?.message || "Failed to save subscription details");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Program Reminder Modal
  const handleOpenReminderModal = (org: OrganizationOverview) => {
    setSelectedOrg(org);
    setCustomMessage("");
    setReminderRecipientEmail(org.contact?.email || "");
    setIsReminderModalOpen(true);
  };

  // Submit Program Reminder
  const handleSendReminder = async () => {
    if (!selectedOrg) return;
    const targetEmail = reminderRecipientEmail.trim() || selectedOrg.contact?.email;
    if (!targetEmail) {
      toast.error("Please enter a valid recipient email address");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`/v2/companies/admin/${selectedOrg.company_id}/send-program-reminder`, {
        recipient_email: targetEmail,
        custom_message: customMessage
      });
      toast.success(res.data.message || "Program reminder sent successfully");
      setIsReminderModalOpen(false);
      fetchOverview();
    } catch (error: any) {
      console.error("Reminder error:", error);
      toast.error(error?.response?.data?.message || "Failed to send program reminder");
    } finally {
      setActionLoading(false);
    }
  };

  // Open Deletion Certificate Customize & Dispatch Modal (Handles PDF Download & Email Send with shared custom data)
  const handleDownloadCertificate = (org: OrganizationOverview) => {
    setSelectedCertOrg(org);
    setIsCertModalOpen(true);
  };

  const handleSendDeletionConfirmation = (org: OrganizationOverview) => {
    setSelectedCertOrg(org);
    setIsCertModalOpen(true);
  };

  // Open Subscription Audit History Modal
  const handleOpenHistoryModal = async (org: OrganizationOverview) => {
    setSelectedOrg(org);
    setIsHistoryModalOpen(true);
    setHistoryLoading(true);
    try {
      let historyData: SubscriptionRecord[] = [];
      try {
        const response = await axios.get(`/v2/companies/admin/${org.company_id}/subscription-history`);
        if (response.data) {
          historyData = response.data.history || [];
        }
      } catch (err: any) {
        const fallbackRes = await axios.get(`/v2/companies/admin/${org.company_id}/subscriptions`);
        if (fallbackRes.data && Array.isArray(fallbackRes.data)) {
          historyData = fallbackRes.data;
        } else if (fallbackRes.data?.subscriptions) {
          historyData = fallbackRes.data.subscriptions;
        }
      }
      setSubHistory(historyData);
    } catch (error: any) {
      console.error("Error loading subscription history:", error);
      toast.error("Failed to load subscription history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // (formatDate and getSmartHealthBadge moved above for sorting & filtering)

  return (
    <div className="space-y-6 pb-20">
      {/* Top Toolbar Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-blue-600 inline-block"></span>
          <h2 className="text-base font-semibold text-slate-900">Organizations Subscription Management</h2>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchOverview}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition shadow-2xs"
          >
            <MdRefresh className={`text-base ${loading ? "animate-spin text-blue-600" : "text-slate-500"}`} />
            Refresh
          </button>

          <button
            onClick={() => handleExportCSV(false)}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition"
          >
            <MdDownload className="text-base" />
            Export Filtered CSV
          </button>
        </div>
      </div>

      {/* Interactive Summary Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Card 1: Total Orgs */}
        <div
          onClick={() => {
            setHealthSignalFilter("all");
            setSubscriptionTypeFilter("all");
            setStatusFilter("all");
            setSearchQuery("");
          }}
          className={`p-3.5 bg-white rounded-xl border transition-all cursor-pointer select-none relative overflow-hidden group ${
            healthSignalFilter === "all" && subscriptionTypeFilter === "all" && statusFilter === "all" && !searchQuery
              ? "border-blue-500 ring-2 ring-blue-500/20 shadow-md"
              : "border-slate-200/80 shadow-2xs hover:shadow-sm hover:border-slate-300"
          }`}
          title="Click to reset filters and view all organizations"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total Orgs</p>
              <h3 className="text-xl font-bold text-slate-900 mt-0.5">{displaySummary.totalOrgs}</h3>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:scale-105 transition-transform">
              <MdBusiness className="text-lg" />
            </div>
          </div>
        </div>

        {/* Card 2: Needs Setup */}
        <div
          onClick={() => {
            setSubscriptionTypeFilter("all");
            setStatusFilter("all");
            setHealthSignalFilter(prev => (prev === "needs_setup" ? "all" : "needs_setup"));
          }}
          className={`p-3.5 bg-white rounded-xl border transition-all cursor-pointer select-none relative overflow-hidden group ${
            healthSignalFilter === "needs_setup" && subscriptionTypeFilter === "all" && statusFilter === "all"
              ? "border-amber-500 ring-2 ring-amber-500/20 shadow-md bg-amber-50/20"
              : "border-slate-200/80 shadow-2xs hover:shadow-sm hover:border-slate-300"
          }`}
          title="Click to filter by organizations needing setup"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-yellow-500"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Needs Setup</p>
              <h3 className="text-xl font-bold text-amber-600 mt-0.5">{displaySummary.unassignedCount || 0}</h3>
            </div>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg group-hover:scale-105 transition-transform">
              <MdWarning className="text-lg" />
            </div>
          </div>
        </div>

        {/* Card 3: Unscheduled Month */}
        <div
          onClick={() => {
            setSubscriptionTypeFilter("all");
            setStatusFilter("all");
            setHealthSignalFilter(prev => (prev === "unscheduled_month" ? "all" : "unscheduled_month"));
          }}
          className={`p-3.5 bg-white rounded-xl border transition-all cursor-pointer select-none relative overflow-hidden group ${
            healthSignalFilter === "unscheduled_month" && subscriptionTypeFilter === "all" && statusFilter === "all"
              ? "border-blue-500 ring-2 ring-blue-500/20 shadow-md bg-blue-50/20"
              : "border-slate-200/80 shadow-2xs hover:shadow-sm hover:border-slate-300"
          }`}
          title="Click to filter by recurring clients with no pentest scheduled this month"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Unscheduled</p>
              <h3 className="text-xl font-bold text-blue-600 mt-0.5">{displaySummary.unscheduledCount || 0}</h3>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:scale-105 transition-transform">
              <MdNotificationsActive className="text-lg" />
            </div>
          </div>
        </div>

        {/* Card 4: Pending Initiation (One-Time) */}
        <div
          onClick={() => {
            setSubscriptionTypeFilter("all");
            setStatusFilter("all");
            setHealthSignalFilter(prev => (prev === "pending_initiation" ? "all" : "pending_initiation"));
          }}
          className={`p-3.5 bg-white rounded-xl border transition-all cursor-pointer select-none relative overflow-hidden group ${
            healthSignalFilter === "pending_initiation" && subscriptionTypeFilter === "all" && statusFilter === "all"
              ? "border-cyan-500 ring-2 ring-cyan-500/20 shadow-md bg-cyan-50/20"
              : "border-slate-200/80 shadow-2xs hover:shadow-sm hover:border-slate-300"
          }`}
          title="Click to filter by one-time engagements not yet started"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-teal-500"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-cyan-700 uppercase tracking-wider">Not Started</p>
              <h3 className="text-xl font-bold text-cyan-600 mt-0.5">{displaySummary.pendingInitiationCount || 0}</h3>
            </div>
            <div className="p-2 bg-cyan-50 text-cyan-600 rounded-lg group-hover:scale-105 transition-transform">
              <MdAssignment className="text-lg" />
            </div>
          </div>
        </div>

        {/* Card 5: Expiring Soon */}
        <div
          onClick={() => {
            setSubscriptionTypeFilter("all");
            setStatusFilter("all");
            setHealthSignalFilter(prev => (prev === "expiring_soon" ? "all" : "expiring_soon"));
          }}
          className={`p-3.5 bg-white rounded-xl border transition-all cursor-pointer select-none relative overflow-hidden group ${
            healthSignalFilter === "expiring_soon" && subscriptionTypeFilter === "all" && statusFilter === "all"
              ? "border-orange-500 ring-2 ring-orange-500/20 shadow-md bg-orange-50/20"
              : "border-slate-200/80 shadow-2xs hover:shadow-sm hover:border-slate-300"
          }`}
          title="Click to filter by subscriptions expiring within 30 days"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-rose-500"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">Expiring</p>
              <h3 className="text-xl font-bold text-orange-600 mt-0.5">{displaySummary.expiringSoon}</h3>
            </div>
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg group-hover:scale-105 transition-transform">
              <MdTimer className="text-lg" />
            </div>
          </div>
        </div>

        {/* Card 6: Active Recurring */}
        <div
          onClick={() => {
            setHealthSignalFilter("all");
            setStatusFilter("all");
            setSubscriptionTypeFilter(prev => (prev === "recurring" ? "all" : "recurring"));
          }}
          className={`p-3.5 bg-white rounded-xl border transition-all cursor-pointer select-none relative overflow-hidden group ${
            subscriptionTypeFilter === "recurring" && healthSignalFilter === "all" && statusFilter === "all"
              ? "border-emerald-500 ring-2 ring-emerald-500/20 shadow-md bg-emerald-50/20"
              : "border-slate-200/80 shadow-2xs hover:shadow-sm hover:border-slate-300"
          }`}
          title="Click to filter by active recurring subscriptions"
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Recurring</p>
              <h3 className="text-xl font-bold text-emerald-600 mt-0.5">{displaySummary.recurringCount}</h3>
            </div>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg group-hover:scale-105 transition-transform">
              <MdAutorenew className="text-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
        {/* Search */}
        <div className="relative w-full md:w-72">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input
            type="text"
            placeholder="Search company or contact..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none text-slate-900 transition"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          {/* Smart Health Signal Filter */}
          <select
            value={healthSignalFilter}
            onChange={e => setHealthSignalFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none font-semibold text-slate-700 transition"
          >
            <option value="all">All Health Signals</option>
            <option value="needs_setup">⚠️ Needs Setup</option>
            <option value="unscheduled_month">📅 Unscheduled Month</option>
            <option value="pending_initiation">🚀 Pending Initiation</option>
            <option value="quota_exhausted">⛔ Quota Exhausted</option>
            <option value="deletion_approaching">⏳ Deletion Approaching</option>
            <option value="retention_expired">📦 Retention Expired</option>
            <option value="expiring_soon">⏰ Expiring Soon</option>
            <option value="healthy">✅ Healthy</option>
          </select>

          {/* Subscription Type Filter */}
          <select
            value={subscriptionTypeFilter}
            onChange={e => setSubscriptionTypeFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none font-medium text-slate-700 transition"
          >
            <option value="all">All Plan Types</option>
            <option value="unassigned">Needs Setup / Unassigned</option>
            <option value="recurring">Recurring (Monthly/Annual)</option>
            <option value="one_time">One-Time Engagement</option>
          </select>

          {/* Expiry Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none font-medium text-slate-700 transition"
          >
            <option value="all">All Expiry Statuses</option>
            <option value="unassigned">Needs Setup / Unassigned</option>
            <option value="active">Active</option>
            <option value="expiring_soon">Expiring Soon</option>
            <option value="expired">Expired</option>
          </select>

          {/* Clear Filters Button (Visible when any filter active) */}
          {(healthSignalFilter !== "all" || subscriptionTypeFilter !== "all" || statusFilter !== "all" || searchQuery) && (
            <button
              onClick={() => {
                setHealthSignalFilter("all");
                setSubscriptionTypeFilter("all");
                setStatusFilter("all");
                setSearchQuery("");
              }}
              className="px-2.5 py-2 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition"
              title="Reset all search and dropdown filters"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Main Table View */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden relative">
        {loading ? (
          <div className="p-16 text-center text-slate-500">
            <MdRefresh className="text-3xl animate-spin mx-auto mb-3 text-blue-600" />
            <p className="text-sm font-medium text-slate-700">Loading organizations data...</p>
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            <MdBusiness className="text-4xl text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-800 mb-1">No organizations match your filters</p>
            <p className="text-xs text-slate-500">Try adjusting your search keyword or dropdown filters.</p>
          </div>
        ) : (
          <div className="w-full">
            <div className="w-full overflow-x-auto custom-scrollbar">
              <table className="w-full min-w-[1140px] text-left text-xs border-collapse">
                <thead className="bg-slate-100/90 text-slate-600 font-bold border-b border-slate-200 uppercase tracking-wider text-[10px] whitespace-nowrap sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-3 w-8 text-center">
                      <input
                        type="checkbox"
                        checked={paginatedOrgs.length > 0 && paginatedOrgs.every(o => selectedOrgIds.includes(o.company_id))}
                        onChange={handleSelectAll}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        title="Select all on this page"
                      />
                    </th>
                    <th
                      onClick={() => handleSort("company_name")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[190px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Organization</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "company_name" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("contact")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[160px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Designated Contact</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "contact" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("subscription")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[110px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Subscription</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "subscription" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("quotas")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[115px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Quotas & Top-Up</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "quotas" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("health_signal")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[165px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Smart Health Signal</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "health_signal" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("expiry")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[130px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Expiry & Retention</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "expiry" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("last_contact")}
                      className="py-3 px-3 cursor-pointer hover:bg-slate-200/60 transition-colors select-none min-w-[135px]"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>Last Reminder</span>
                        <span className="text-[10px] text-slate-400">
                          {sortField === "last_contact" ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </div>
                    </th>
                    <th className="py-3 px-3 text-right min-w-[145px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedOrgs.map(org => {
                    const isUnassigned = org.is_unassigned || org.status === "unassigned" || org.subscription_type.toLowerCase() === "unassigned";
                    const isSelected = selectedOrgIds.includes(org.company_id);
                    const smartBadge = getSmartHealthBadge(org);

                    return (
                      <tr
                        key={org.company_id}
                        onClick={() => handleRowClick(org.company_id)}
                        className={`hover:bg-blue-50/50 cursor-pointer transition-colors group ${
                          isSelected ? "bg-blue-50/70" : ""
                        }`}
                        title="Click to view company subscription history and linked components"
                      >
                        {/* Checkbox */}
                        <td className="py-3 px-3 text-center align-middle" onClick={(e) => handleToggleSelectOrg(org.company_id, e)}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                        </td>

                        {/* Org Name & Logo */}
                        <td className="py-3 px-3 align-middle">
                          <div className="flex items-center gap-2.5">
                            {org.company_logo ? (
                              <img
                                src={org.company_logo}
                                alt={org.company_name}
                                className="w-8 h-8 rounded-lg object-cover border border-slate-200 shadow-2xs group-hover:border-blue-300 transition-colors shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold flex items-center justify-center shadow-xs text-xs shrink-0 group-hover:scale-105 transition-transform">
                                {org.company_name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-bold text-slate-900 text-xs group-hover:text-blue-600 transition-colors truncate">{org.company_name}</div>
                              <div className="text-[10px] font-semibold text-blue-600/90 truncate max-w-[180px]" title={org.subscription_name || `${org.company_name} Subscription`}>
                                {org.subscription_name || `${org.company_name} Subscription`}
                              </div>
                              <div className="text-[9px] font-mono font-medium text-slate-400 bg-slate-100 px-1 py-0.2 rounded border border-slate-200/60 inline-block mt-0.5">
                                ID: #{org.company_id}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Designated Contact */}
                        <td className="py-3 px-3 align-middle">
                          {org.contact ? (
                            <div className="min-w-0">
                              <div className="text-slate-800 font-semibold text-xs truncate">{org.contact.name}</div>
                              <div className="text-[10px] text-slate-500 font-mono truncate max-w-[170px]" title={org.contact.email}>
                                {org.contact.email}
                              </div>
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400 italic">No contact</span>
                          )}
                        </td>

                        {/* Subscription Type */}
                        <td className="py-3 px-3 align-middle whitespace-nowrap">
                          {isUnassigned ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                              <MdWarning className="text-xs text-amber-600" />
                              Unassigned
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold capitalize border ${
                                org.is_one_time
                                  ? "bg-purple-50 text-purple-700 border-purple-200"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
                              }`}
                            >
                              {org.is_one_time ? <MdAssignment className="text-xs" /> : <MdAutorenew className="text-xs" />}
                              {org.subscription_type}
                            </span>
                          )}
                        </td>

                        {/* Quotas & Quick Top-Up */}
                        <td className="py-3 px-3 align-middle whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <div className="flex flex-col gap-0.5 text-[10px]">
                              <span className="text-slate-600">Comp: <strong className="text-slate-900">{org.comprehensive_runs_quota}</strong></span>
                              <span className="text-slate-600">Quick: <strong className="text-slate-900">{org.quick_runs_quota}</strong></span>
                            </div>
                            {!isUnassigned && (
                              <button
                                onClick={(e) => handleOpenTopUpModal(org.company_id, org.company_name, e)}
                                title="Quick Top-Up Quota (+1 Run)"
                                className="p-1.5 text-blue-600 hover:bg-blue-100/70 bg-blue-50 border border-blue-200 rounded-md transition font-semibold flex items-center gap-0.5 text-[10px]"
                              >
                                <MdFlashOn className="text-xs" />
                                Top Up
                              </button>
                            )}
                          </div>
                        </td>

                        {/* Smart Health Badge & Quick Inline Actions */}
                        <td className="py-3 px-3 align-middle whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold border ${smartBadge.bg}`}
                              title={smartBadge.hint}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${smartBadge.dot}`} />
                              {smartBadge.label}
                            </span>

                            {/* Quick Inline 1-Click Reminder Nudge */}
                            {smartBadge.key === "unscheduled_month" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenReminderModal(org);
                                }}
                                title="1-Click Send Reminder Email"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition shadow-2xs"
                              >
                                <MdNotificationsActive className="text-xs text-blue-600" />
                                <span>Nudge</span>
                              </button>
                            )}

                            {/* Quick Inline 1-Click Deletion Notice */}
                            {(smartBadge.key === "deletion_approaching" || smartBadge.key === "retention_expired") && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeletionModal(org);
                                }}
                                title="1-Click Send Deletion Notice"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 transition shadow-2xs"
                              >
                                <MdEmail className="text-xs text-purple-600" />
                                <span>Notice</span>
                              </button>
                            )}
                          </div>
                        </td>

                        {/* Expiry Date & Retention */}
                        <td className="py-3 px-3 align-middle whitespace-nowrap">
                          <div>
                            <div className="text-slate-800 font-semibold text-xs">
                              {org.status === "unassigned" ? "N/A" : formatDate(org.subscription_end_date)}
                            </div>
                            {org.retention_end_date && (
                              <div className="text-[10px] text-purple-700 font-medium mt-0.5">
                                12-Mo Retention: {formatDate(org.retention_end_date)}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Last Contact Log */}
                        <td className="py-3 px-3 align-middle whitespace-nowrap">
                          <div className="space-y-0.5 text-[10px]">
                            {org.last_deletion_notice_sent_at && (
                              <div className="text-purple-700 font-medium flex items-center gap-1" title={`Notice sent on ${formatDate(org.last_deletion_notice_sent_at)}`}>
                                <MdEmail className="text-xs shrink-0 text-purple-600" /> Notice: {formatDate(org.last_deletion_notice_sent_at)}
                              </div>
                            )}
                            {org.last_program_reminder_sent_at && (
                              <div className="text-blue-700 font-medium flex items-center gap-1" title={`Reminder sent on ${formatDate(org.last_program_reminder_sent_at)}`}>
                                <MdNotificationsActive className="text-xs shrink-0 text-blue-600" /> Remind: {formatDate(org.last_program_reminder_sent_at)}
                              </div>
                            )}
                            {!org.last_deletion_notice_sent_at && !org.last_program_reminder_sent_at && (
                              <span className="text-slate-400 italic" title="No kickoff reminder or deletion notice email has been dispatched yet">
                                No notices sent
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-3 text-right align-middle whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            {isUnassigned ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleOpenRenewModal(org); }}
                                className="px-2.5 py-1 text-[11px] font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 rounded-lg transition shadow-xs flex items-center gap-1"
                                title="Assign Subscription Term & Quotas"
                              >
                                <MdAdd className="text-sm" />
                                Assign Term
                              </button>
                            ) : (
                              <>
                                {/* Important Action: Renew / Edit */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenRenewModal(org); }}
                                  title="Edit / Renew Current Subscription"
                                  className="px-2.5 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition flex items-center gap-1 shadow-2xs"
                                >
                                  <MdAutorenew className="text-sm text-emerald-600" />
                                  <span>Renew</span>
                                </button>

                                {/* Primary Action: Open All Actions in Right Slide Drawer */}
                                <button
                                  onClick={(e) => handleOpenQuickView(org, e)}
                                  title="Open Organization Actions & Quick View Slide"
                                  className="px-2.5 py-1 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-lg transition flex items-center gap-1.5 shadow-xs"
                                >
                                  <MdVisibility className="text-sm" />
                                  <span>Actions</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination & Rows-Per-Page Controls */}
            <div className="p-3.5 bg-slate-50/80 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
              <div className="flex items-center gap-3">
                <span>
                  Showing <strong className="text-slate-900">{totalItems === 0 ? 0 : startIndex + 1}</strong> to{" "}
                  <strong className="text-slate-900">{Math.min(startIndex + pageSize, totalItems)}</strong> of{" "}
                  <strong className="text-slate-900">{totalItems}</strong> organization{totalItems === 1 ? "" : "s"}
                </span>
                <div className="flex items-center gap-1.5 pl-3 border-l border-slate-300">
                  <span className="text-slate-400 text-[11px]">Rows:</span>
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="bg-white border border-slate-200 rounded px-1.5 py-0.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium cursor-pointer"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={safeCurrentPage <= 1}
                    className="px-2.5 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition text-xs"
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-7 h-7 rounded text-xs font-semibold transition ${
                        safeCurrentPage === page
                          ? "bg-blue-600 text-white shadow-2xs"
                          : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={safeCurrentPage >= totalPages}
                    className="px-2.5 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition text-xs"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Bulk Actions Bar */}
      {selectedOrgIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[8000] bg-slate-900/90 text-white backdrop-blur-md px-6 py-3.5 rounded-2xl shadow-2xl border border-slate-700/80 flex items-center gap-4 animate-bounce-short">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-xs">
              {selectedOrgIds.length}
            </span>
            <span className="text-xs font-semibold text-slate-200">Organizations Selected</span>
          </div>

          <div className="h-4 w-px bg-slate-700"></div>

          {/* Only show Send Bulk Reminders if there are recurring organizations applicable */}
          {reminderEligibleOrgs.length > 0 && (
            <button
              onClick={handleBulkSendReminders}
              disabled={actionLoading}
              className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title={`Send monthly program scheduling reminder to ${reminderEligibleOrgs.length} recurring organization(s)`}
            >
              <MdNotificationsActive className="text-sm" />
              {actionLoading ? "Sending..." : `Send Bulk Reminders (${reminderEligibleOrgs.length})`}
            </button>
          )}

          {/* Only show Send Deletion Notices if there are one-time retention organizations applicable */}
          {deletionEligibleOrgs.length > 0 && (
            <button
              onClick={handleBulkSendDeletionNotices}
              disabled={actionLoading}
              className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title={`Send 12-month retention deletion notice to ${deletionEligibleOrgs.length} one-time engagement organization(s)`}
            >
              <MdEmail className="text-sm" />
              {actionLoading ? "Sending..." : `Send Deletion Notices (${deletionEligibleOrgs.length})`}
            </button>
          )}

          {/* If all selected organizations are Needs Setup / unassigned */}
          {reminderEligibleOrgs.length === 0 && deletionEligibleOrgs.length === 0 && (
            <span className="text-[11px] text-amber-300 bg-amber-950/60 px-2.5 py-1 rounded-xl border border-amber-800/60 font-medium flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
              Needs Setup (No reminder or deletion notice applicable)
            </span>
          )}

          <button
            onClick={() => handleExportCSV(true)}
            className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-semibold transition flex items-center gap-1.5"
          >
            <MdDownload className="text-sm" />
            Export Selected CSV
          </button>

          <button
            onClick={() => setSelectedOrgIds([])}
            className="p-1 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition"
            title="Clear Selection"
          >
            <MdClose className="text-lg" />
          </button>
        </div>
      )}

      {/* Quick View Inspection Drawer Component */}
      <CompanyQuickViewDrawer
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        companyId={quickViewCompanyId}
        companyName={quickViewCompanyName}
        org={quickViewOrg}
        onOpenTopUp={(id, name) => handleOpenTopUpModal(id, name)}
        onNavigateToCompany={(id) => handleRowClick(id)}
        onOpenRenewModal={(org) => handleOpenRenewModal(org)}
        onOpenAddSubscriptionModal={(org) => handleOpenAddSubscriptionModal(org)}
        onOpenDeletionModal={(org) => handleOpenDeletionModal(org)}
        onSendDeletionConfirmation={(org) => handleSendDeletionConfirmation(org)}
        onOpenReminderModal={(org) => handleOpenReminderModal(org)}
        onOpenHistoryModal={(org) => handleOpenHistoryModal(org)}
        onDownloadCertificate={(org) => handleDownloadCertificate(org)}
      />

      {/* MODAL: Quick Quota Top-Up */}
      {isTopUpModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 overflow-hidden animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <MdFlashOn className="text-blue-600 text-xl" />
                Quick Quota Top-Up — {topUpCompanyName}
              </h3>
              <button
                onClick={() => setIsTopUpModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Comprehensive Runs Quotas to Add
                </label>
                <div className="flex gap-2 mb-2">
                  {[1, 3, 5].map(num => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setTopUpCompAdd(num)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition ${
                        topUpCompAdd === num
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      +{num} Run{num > 1 ? "s" : ""}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={0}
                  value={topUpCompAdd}
                  onChange={e => setTopUpCompAdd(Number(e.target.value))}
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none text-slate-900 font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Quick Runs Quotas to Add
                </label>
                <div className="flex gap-2 mb-2">
                  {[0, 1, 3, 5].map(num => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setTopUpQuickAdd(num)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition ${
                        topUpQuickAdd === num
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      +{num} Run{num > 1 ? "s" : ""}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={0}
                  value={topUpQuickAdd}
                  onChange={e => setTopUpQuickAdd(Number(e.target.value))}
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white focus:outline-none text-slate-900 font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Top-Up Reason / Reference Note
                </label>
                <input
                  type="text"
                  value={topUpReason}
                  onChange={e => setTopUpReason(e.target.value)}
                  placeholder="e.g. Sales upgrade, mid-term top up, customer request"
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none text-slate-900"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setIsTopUpModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleTopUpSubmit}
                disabled={actionLoading}
                className="px-4 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 shadow-xs transition"
              >
                {actionLoading && <MdRefresh className="animate-spin text-base" />}
                Submit Quota Top-Up
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MODAL 1: Send Deletion Notice */}
      {isDeletionModalOpen && selectedOrg && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <MdEmail className="text-purple-600" />
                Send Retention & Deletion Notice
              </h3>
              <button
                onClick={() => setIsDeletionModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs text-slate-700 overflow-y-auto">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
                <div>
                  <span className="font-semibold text-slate-900">Organization:</span> {selectedOrg.company_name}
                </div>
                <div>
                  <span className="font-semibold text-slate-900">Designated Contact:</span>{" "}
                  {selectedOrg.contact?.email || "No contact found"}
                </div>
                <div>
                  <span className="font-semibold text-slate-900">12-Month Retention Date:</span>{" "}
                  {formatDate(selectedOrg.retention_end_date)}
                </div>
              </div>

              <div className="p-3.5 bg-purple-50 text-purple-800 rounded-xl border border-purple-100 text-xs leading-relaxed">
                This email notifies the client that their 12-month retention period is expiring. It includes direct links for downloading official <strong>Pentest Reports (/dashboard/pentest-reports)</strong> and <strong>Vulnerability Reports (/dashboard/reports)</strong> before permanent deletion.
              </div>

              {/* Program Scope Selection */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MdBugReport className="text-purple-600 text-base" />
                    <span className="font-bold text-xs text-slate-800">
                      Select Program(s) for Notice
                    </span>
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-purple-100 text-purple-700">
                      {selectedDeletionProgramIds.length} of {deletionPrograms.length} selected
                    </span>
                  </div>
                  {deletionPrograms.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllDeletionPrograms}
                      className="text-[11px] font-bold text-purple-600 hover:text-purple-800 transition"
                    >
                      {selectedDeletionProgramIds.length === deletionPrograms.length
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  )}
                </div>

                {deletionPrograms.length === 0 ? (
                  <div className="text-[11px] text-slate-500 bg-white p-2 rounded-lg border border-slate-200/60">
                    No active pentest programs found under this subscription. Default title will be used.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto pr-1">
                    {deletionPrograms.map(p => {
                      const isChecked = selectedDeletionProgramIds.includes(p.program_id);
                      return (
                        <div
                          key={p.program_id}
                          onClick={() => handleToggleDeletionProgram(p.program_id)}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition select-none ${
                            isChecked
                              ? "bg-purple-50 border-purple-300 text-purple-900 shadow-2xs"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5 pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-xs truncate">
                              {p.program_title || `Program #${p.program_id}`}
                            </p>
                            {p.createdAt && (
                              <p className="text-[10px] text-slate-400">
                                {formatDate(p.createdAt)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                    Engagement Title in Email Notice
                  </label>
                  <input
                    type="text"
                    value={customDeletionProgramTitle}
                    onChange={e => setCustomDeletionProgramTitle(e.target.value)}
                    placeholder="e.g. Capture The Bug Penetration Test"
                    className="w-full p-2 text-xs bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none text-slate-900 font-semibold transition"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    This program title is highlighted in the client's email notification.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={deletionRecipientEmail}
                  onChange={e => setDeletionRecipientEmail(e.target.value)}
                  placeholder="contact@company.com"
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:bg-white focus:outline-none text-slate-900 transition"
                />
                {!selectedOrg.contact?.email && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    No primary contact email was registered for this organization. You can specify a recipient email above.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Custom Message (Optional)
                </label>
                <textarea
                  rows={3}
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Add any specific instructions for the client..."
                  className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:bg-white focus:outline-none text-slate-900 transition"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setIsDeletionModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSendDeletionNotice}
                disabled={actionLoading || !deletionRecipientEmail.trim()}
                className="px-4 py-2 text-xs font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-xs transition"
              >
                {actionLoading && <MdRefresh className="animate-spin text-base" />}
                Send Deletion Notice Email
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MODAL 2: Renew / Assign Subscription */}
      {isRenewModalOpen && selectedOrg && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                {isAddNewSub ? <MdAdd className="text-blue-600" /> : <MdAutorenew className="text-emerald-600" />}
                {isAddNewSub
                  ? "Add New Independent Subscription Term"
                  : selectedOrg.is_unassigned
                  ? "Assign Subscription & Quotas"
                  : "Renew / Update Current Subscription"} — {selectedOrg.company_name}
              </h3>
              <button
                onClick={() => setIsRenewModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              {/* Subscription Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Subscription Name
                </label>
                <input
                  type="text"
                  value={renewSubName}
                  onChange={e => setRenewSubName(e.target.value)}
                  placeholder={`e.g. ${selectedOrg.company_name} Subscription`}
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white focus:outline-none text-slate-900 font-semibold transition"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Default placeholder auto-populated with company name. Admin & SubAdmin can customize.
                </p>
              </div>

              {/* Subscription Type */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Subscription Term / Type
                </label>
                <select
                  value={renewType}
                  onChange={e => setRenewType(e.target.value)}
                  className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white focus:outline-none text-slate-900 transition"
                >
                  <option value="monthly">Monthly Recurring</option>
                  <option value="quarterly">Quarterly Recurring</option>
                  <option value="half_yearly">Half-Yearly Recurring</option>
                  <option value="yearly">Yearly Recurring</option>
                  <option value="one-time">One-Time Engagement</option>
                  <option value="pilot">Pilot Program</option>
                </select>
              </div>

              {/* Start & End Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={renewStartDate}
                    onChange={e => setRenewStartDate(e.target.value)}
                    className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white focus:outline-none text-slate-900 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Expiry Date</label>
                  <input
                    type="date"
                    value={renewEndDate}
                    onChange={e => setRenewEndDate(e.target.value)}
                    className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white focus:outline-none text-slate-900 transition"
                  />
                </div>
              </div>

              {/* Quotas */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Quick Runs Quota</label>
                  <input
                    type="number"
                    min={0}
                    value={renewQuickQuota}
                    onChange={e => setRenewQuickQuota(Number(e.target.value))}
                    className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white focus:outline-none text-slate-900 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Comprehensive Quota</label>
                  <input
                    type="number"
                    min={0}
                    value={renewCompQuota}
                    onChange={e => setRenewCompQuota(Number(e.target.value))}
                    className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white focus:outline-none text-slate-900 transition"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setIsRenewModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRenewSubscription}
                disabled={actionLoading}
                className={`px-4 py-2 text-xs font-semibold text-white rounded-xl disabled:opacity-50 flex items-center gap-2 shadow-xs transition ${
                  isAddNewSub ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {actionLoading && <MdRefresh className="animate-spin text-base" />}
                {isAddNewSub
                  ? "Create & Save New Subscription Term"
                  : selectedOrg.is_unassigned
                  ? "Assign Subscription"
                  : "Save & Renew Current Subscription"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MODAL 3: Send Program Reminder */}
      {isReminderModalOpen && selectedOrg && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <MdNotificationsActive className="text-blue-600" />
                Send Monthly Program Reminder
              </h3>
              <button
                onClick={() => setIsReminderModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs text-slate-700">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
                <div>
                  <span className="font-semibold text-slate-900">Organization:</span> {selectedOrg.company_name}
                </div>
                <div>
                  <span className="font-semibold text-slate-900">Designated Contact:</span>{" "}
                  {selectedOrg.contact?.email || "No contact found"}
                </div>
              </div>

              <div className="p-3.5 bg-blue-50 text-blue-800 rounded-xl border border-blue-100 text-xs leading-relaxed">
                Send a manual nudge email asking the organization to schedule their security assessment program for the current calendar month.
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={reminderRecipientEmail}
                  onChange={e => setReminderRecipientEmail(e.target.value)}
                  placeholder="contact@company.com"
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none text-slate-900 transition"
                />
                {!selectedOrg.contact?.email && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    No primary contact email was registered for this organization. You can specify a recipient email above.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Custom Note (Optional)
                </label>
                <textarea
                  rows={3}
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Add custom notes..."
                  className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none text-slate-900 transition"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => setIsReminderModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReminder}
                disabled={actionLoading || !reminderRecipientEmail.trim()}
                className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 shadow-xs transition"
              >
                {actionLoading && <MdRefresh className="animate-spin text-base" />}
                Send Program Reminder
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MODAL 4: Subscription Audit History */}
      {isHistoryModalOpen && selectedOrg && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <MdHistory className="text-slate-700" />
                Subscription Audit History — {selectedOrg.company_name}
              </h3>
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="p-6 text-xs text-slate-700 max-h-[60vh] overflow-y-auto">
              {historyLoading ? (
                <div className="p-10 text-center text-slate-500">
                  <MdRefresh className="text-2xl animate-spin mx-auto mb-2 text-slate-600" />
                  <p>Loading subscription history...</p>
                </div>
              ) : subHistory.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-xl border border-slate-200 text-slate-500">
                  <MdHistory className="text-3xl mx-auto mb-2 text-slate-400" />
                  <p className="font-medium text-slate-800">No subscription history entries found</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    As subscription terms and quotas are updated over time, each historic interval will be logged here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Historic Subscriptions & Quota Assignments ({subHistory.length})
                  </p>
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                    {subHistory.map((item, idx) => (
                      <div key={item.subscription_id || idx} className="p-3.5 bg-white hover:bg-slate-50/50 transition">
                        <div className="flex items-center justify-between mb-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold capitalize bg-emerald-50 text-emerald-800 border border-emerald-200">
                            {item.subscription_type}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono">
                            Recorded: {new Date(item.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                          <div>
                            <span className="text-slate-400 block text-[10px]">Start Date</span>
                            <span className="font-medium text-slate-800">{item.start_date ? new Date(item.start_date).toLocaleDateString() : "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Expiry Date</span>
                            <span className="font-medium text-slate-800">{item.end_date ? new Date(item.end_date).toLocaleDateString() : "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Comp Quota</span>
                            <span className="font-bold text-slate-900">{item.comprehensive_runs_quota}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Quick Quota</span>
                            <span className="font-bold text-slate-900">{item.quick_runs_quota}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Deletion Certificate Customize & Dispatch Modal */}
      <DeletionCertificateModal
        isOpen={isCertModalOpen}
        onClose={() => setIsCertModalOpen(false)}
        org={selectedCertOrg}
        onSuccess={fetchOverview}
      />
    </div>
  );
};

export default OrganizationsSubscriptions;
